import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import EventCreateForm from '../components/events/EventCreateForm';
import SocietyContextMobileNav from '../components/societies/SocietyContextMobileNav';
import { eventService } from '../services/eventService';
import { getSlugFromParams } from '../utils/urlHelpers';

const EventCreatePage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const eventIdentifier = getSlugFromParams(params, 'event');
  const societySlug = params.societySlug;
  const isEditMode = location.pathname.includes('/edit') && !!eventIdentifier;
  const societyHubNav = societySlug ? (
    <SocietyContextMobileNav societySlug={societySlug} activeSection="events" />
  ) : null;

  const [initialEvent, setInitialEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(isEditMode);
  const [eventLoadError, setEventLoadError] = useState(null);

  useEffect(() => {
    if (!isEditMode) return;
    const loadEvent = async () => {
      try {
        setLoadingEvent(true);
        setEventLoadError(null);
        const data = await eventService.getEvent(eventIdentifier);
        setInitialEvent(data);
      } catch (err) {
        console.error('Error loading event for edit:', err);
        setEventLoadError('Failed to load event');
      } finally {
        setLoadingEvent(false);
      }
    };
    loadEvent();
  }, [isEditMode, eventIdentifier]);

  if (isEditMode && loadingEvent) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen bg-gray-50">
          {societyHubNav}
          <div className="py-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
              <p className="mt-4 text-gray-600">Loading event...</p>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (isEditMode && eventLoadError) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen bg-gray-50">
          {societyHubNav}
          <div className="py-8 flex items-center justify-center">
            <div className="text-center">
              <p className="text-red-600">{eventLoadError}</p>
              <button
                type="button"
                onClick={() => navigate(societySlug ? `/societies/${societySlug}/events` : '/events')}
                className="mt-4 text-blue-600 hover:underline"
              >
                Back to Events
              </button>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="PakPoints">
      <Helmet>
        <title>{isEditMode ? 'Edit Event' : 'Create Event'} - PakPoints</title>
        <meta
          name="description"
          content={
            isEditMode
              ? 'Edit your community event on PakPoints'
              : 'Create a new event to share with your community on PakPoints'
          }
        />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {societyHubNav}
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <EventCreateForm initialEvent={initialEvent} isEditMode={isEditMode} />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default EventCreatePage;
